import React, {FC} from "react";
import useSafeState from "../../utils/safe-state";
import {openNotification} from "../../utils/openNotification";
import {useAxios} from "../../utils/hooks";
import {Redirect} from "react-router-dom";
import {Button, Form, Steps} from "antd";
import {Trans} from "@lingui/macro";
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import '../../styles/OnboardingFinish.css';
import OnboardingFinishedProduct from "../../components/onboarding/onboarding-finished-product";
import OnboardingProfile from "../../components/onboarding/onboarding-profile";

import {
    CodeSandboxOutlined,
    LeftOutlined,
    ProfileOutlined, RightOutlined,
} from "@ant-design/icons";
import moment from "moment";
import ButtonWithIcon from "../../components/buttons/button-with-icon";
import ButtonWithIconSuff from "../../components/buttons/button-with-icon-suff";
import OnboardingFirstStep from "../../components/onboarding/onboarding-first-step";
import OnboardingSecondStep from "../../components/onboarding/onboarding-second-step";



const OnboardingFinishScreen: FC = () => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [currentState, setCurrentState] = useSafeState(0);
    const [onboardingStatus, setOnboardingStatus] = React.useState<string | null>(null);
    const { Step } = Steps;
    const [redirect, setRedirect] = React.useState(false);
    const [form] = Form.useForm();

    React.useEffect(() => {

        if (axiosInstance.current != null) {

            axiosInstance.current
                .get('/resource/onboarding/get-onboarding-status')
                .then((response) => {
                    setOnboardingStatus(response.data);
                })
                .catch((error: any) => {
                    openNotification('error', error);
                });
        }
    }, []);

    const handleFinish = () => {
        saveSecondStep();
    };

    const handleNext = () => {
        switch(currentState) {
            case 0:
                saveFirstStep(1);
                break;
            case 1:
                break;
        }
    };

    const handlePrev = () => {
        setCurrentState(currentState - 1);
        handleScrollToTop();
    };

    const saveFirstStep = (nextPageIdx: any) => {
        const collectInfo = form.getFieldValue(["onboarding", "profile", "collectInfo"]);
        const riskAssessment = form.getFieldValue(["onboarding", "profile", "riskAssessment"]);
        const helpfulForIdentifyingCircumventionRisks = form.getFieldValue(["onboarding", "profile", "helpfulForIdentifyingCircumventionRisks"]);

        const fieldsToValidate: any[] = [
            ["onboarding", "profile", "sme"],
            ["onboarding", "profile", "collectInfo"],
            ["onboarding", "profile", "riskAssessment"]
        ];

        if (collectInfo === 2) {
            fieldsToValidate.push(["onboarding", "profile", "collectInfoDeadline"]);
        }

        if (riskAssessment === 3) {
            fieldsToValidate.push(["onboarding", "profile", "riskAssessmentDescription"]);
            fieldsToValidate.push(["onboarding", "profile", "riskAssessmentDeadline"]);
        }

        form.validateFields(fieldsToValidate).then(() => {
            if (axiosInstance.current != null) {
                axiosInstance.current
                    .post('/resource/onboarding-data/save-first-step', {
                        sme: form.getFieldValue(["onboarding", "profile", "sme"]),
                        collectInfo: form.getFieldValue(["onboarding", "profile", "collectInfo"]),
                        collectInfoDeadline: form.getFieldValue(["onboarding", "profile", "collectInfoDeadline"]) != null
                            ? moment(form.getFieldValue(["onboarding", "profile", "collectInfoDeadline"])).format('YYYY-MM-DD') : null,
                        riskAssessment: form.getFieldValue(["onboarding", "profile", "riskAssessment"]),
                        riskAssessmentDescription: form.getFieldValue(["onboarding", "profile", "riskAssessmentDescription"]),
                        riskAssessmentDeadline: form.getFieldValue(["onboarding", "profile", "riskAssessmentDeadline"]) != null
                            ? moment(form.getFieldValue(["onboarding", "profile", "riskAssessmentDeadline"])).format('YYYY-MM-DD') : null
                    }, {})
                    .then(() => {
                        setCurrentState(currentState + nextPageIdx);
                        handleScrollToTop();
                    })
                    .catch(function (error: any) {
                        openNotification('error', error);
                    });
            }
        }).catch((errorInfo) => {
            console.log('Validation failed:', errorInfo);
        });
    }

    const saveSecondStep = () => {

        const helpfulForIdentifyingCircumventionRisks = form.getFieldValue(["onboarding", "profile", "helpfulForIdentifyingCircumventionRisks"]);

        const fieldsToValidate: any[] = [
            ["onboarding", "profile", "goodsMixingCounterMeasureDeadline"],
            ["onboarding", "profile", "goodsMixingCounterMeasure"],
            ["onboarding", "profile", "rawMatGoodsMixingCounterMeasureDeadline"],
            ["onboarding", "profile", "rawMatGoodsMixingCounterMeasure"],
            ["onboarding", "profile", "helpfulForIdentifyingCircumventionRisks"],
            ["onboarding", "profile", "helpfulForIdentifyingCircumventionRisksDescription"],
            ["onboarding", "profile", "haveAnyConcerns"],
            ["onboarding", "profile", "haveAnyEarlyStageInvestigation"]
        ];

        if (helpfulForIdentifyingCircumventionRisks === 1) {
            fieldsToValidate.push(["onboarding", "profile", "helpfulForIdentifyingCircumventionRisksDescription"]);
        }

        form.validateFields(fieldsToValidate).then(() => {
            if (axiosInstance.current != null) {
                axiosInstance.current
                    .post('/resource/onboarding-data/save-second-step', {
                        goodsMixingCounterMeasure: form.getFieldValue(["onboarding", "profile", "goodsMixingCounterMeasure"]),
                        goodsMixingCounterMeasureDeadline: form.getFieldValue(["onboarding", "profile", "goodsMixingCounterMeasureDeadline"]) != null
                            ? moment(form.getFieldValue(["onboarding", "profile", "goodsMixingCounterMeasureDeadline"])).format('YYYY-MM-DD') : null,
                        rawMatGoodsMixingCounterMeasure: form.getFieldValue(["onboarding", "profile", "rawMatGoodsMixingCounterMeasure"]),
                        rawMatGoodsMixingCounterMeasureDeadline: form.getFieldValue(["onboarding", "profile", "rawMatGoodsMixingCounterMeasureDeadline"]) != null
                            ? moment(form.getFieldValue(["onboarding", "profile", "rawMatGoodsMixingCounterMeasureDeadline"])).format('YYYY-MM-DD') : null,
                        helpfulForIdentifyingCircumventionRisks: form.getFieldValue(["onboarding", "profile", "helpfulForIdentifyingCircumventionRisks"]),
                        helpfulForIdentifyingCircumventionRisksDescription: form.getFieldValue(["onboarding", "profile", "helpfulForIdentifyingCircumventionRisksDescription"]),
                        haveAnyConcerns: form.getFieldValue(["onboarding", "profile", "haveAnyConcerns"]),
                        haveAnyEarlyStageInvestigation: form.getFieldValue(["onboarding", "profile", "haveAnyEarlyStageInvestigation"]),
                        knownRisksForSpecificProducts: form.getFieldValue(["onboarding", "profile", "knownRisksForSpecificProducts"]),
                        certId: form.getFieldValue(["onboarding", "profile", "certId"]),
                        certIdDescription: form.getFieldValue(["onboarding", "profile", "certIdDescription"])
                    }, {})
                    .then(() => {

                        if (axiosInstance.current != null) {
                            axiosInstance.current
                                .post('/resource/onboarding/save-final-onboarding-data', {}, {}
                                )
                                .then(() => {
                                    window.location.reload();
                                })
                                .catch(function (error: any) {
                                    openNotification('error', error);
                                });
                        }

                    })
                    .catch(function (error: any) {
                        openNotification('error', error);
                    });
            }
        }).catch((errorInfo) => {
            console.log('Validation failed:', errorInfo);
        });
    }

    const handleScrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "auto", // instant scroll
        });
    };

    function renderContent(state: any) {
        switch (state) {
            case 0:
                return <OnboardingFirstStep form={form} />;
            case 1:
                return <OnboardingSecondStep form={form} />;
            default:
                return <>What?</>;
        }
    }

    return (
            <>
                { onboardingStatus == 'COMPLETED' ? <Redirect to={"/dashboard"} /> : <></>}
                <div className="center-card">
                    <Steps current={currentState} style={{marginBottom: 30, width: 300}}>
                        <Step title={<Trans>Step 1</Trans>} />
                        <Step title={<Trans>Step 2</Trans>} />
                    </Steps>
                </div>
                <div className="center-card">
                    {renderContent(currentState)}
                </div>

                <Button disabled={currentState != 1} htmlType="button" type="primary"
                        onClick={() => handleFinish()} style={{float: 'right', marginRight: 10, marginTop: 40}}>
                    <Trans>Finish</Trans>
                </Button>

                <Button disabled={currentState == 1} htmlType="button"
                        onClick={() => handleNext()}style={{float: 'right', marginRight: 10, marginTop: 40}}>
                    <ButtonWithIconSuff
                        icon={<RightOutlined />}
                        text={<Trans>Next</Trans>}
                    />
                </Button>

                <Button disabled={currentState == 0} htmlType="button"
                        onClick={() => handlePrev()} style={{float: 'right', marginRight: 10, marginTop: 40}}>
                    <ButtonWithIcon
                        icon={<LeftOutlined />}
                        text={<Trans>Back</Trans>}
                    />
                </Button>
            </>
    );
}
export default OnboardingFinishScreen;